import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Status badges: tinted background + border; label text uses slate-900 so it does not
 * match the semantic hue (same pattern as amber warning).
 * For block-level alerts, use `Alert` — those use `border-2`.
 */
type BadgeVariant = "success" | "warning" | "info" | "danger" | "neutral" | "purple";

const variantClasses: Record<BadgeVariant, string> = {
  success: "border border-emerald-200 bg-emerald-50 text-slate-900 hover:bg-emerald-50",
  warning: "border border-amber-200 bg-amber-50 text-slate-900 hover:bg-amber-50",
  info: "border border-blue-200 bg-blue-50 text-slate-900 hover:bg-blue-50",
  danger: "border border-red-200 bg-red-50 text-slate-900 hover:bg-red-50",
  neutral: "border border-slate-200 bg-slate-100 text-slate-900 hover:bg-slate-100",
  purple: "border border-purple-200 bg-purple-50 text-slate-900 hover:bg-purple-50",
};

interface StatusBadgeProps {
  variant: BadgeVariant;
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

export function StatusBadge({ variant, children, icon, className }: StatusBadgeProps) {
  return (
    <Badge className={cn("font-medium shadow-none", variantClasses[variant], className)}>
      {icon && <span className="mr-1 text-slate-600 [&_svg]:text-slate-600">{icon}</span>}
      {children}
    </Badge>
  );
}

/* ── Library-card status mapping ─────────────────────────── */

const cardStatusMap: Record<string, { variant: BadgeVariant; label: string }> = {
  PENDING: { variant: "neutral", label: "Pending" },
  PROCESSING: { variant: "info", label: "Processing" },
  READY: { variant: "success", label: "Ready" },
  PICKED_UP: { variant: "purple", label: "Picked Up" },
  EXPIRED: { variant: "danger", label: "Expired" },
};

export function CardStatusBadge({ status }: { status: string }) {
  const cfg = cardStatusMap[status] ?? { variant: "neutral" as const, label: status };
  return <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>;
}
